/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Field, FieldProps } from 'formik';
import { FormTextField } from 'components/TextField';

import { ssnMask } from 'utils/masks';
import { useSelector } from 'react-redux';
import { selectLockedFields } from 'state/creditApplication';
import useFieldCustomization from 'hooks/useFieldCustomization';
import withOptional from './withOptional';
import InputAdornment from '@material-ui/core/InputAdornment/InputAdornment';
import IconButton from '@material-ui/core/IconButton/IconButton';
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import useTranslate from 'hooks/useTranslate';

interface PropTypes {
  fieldId?: string;
  fieldName?: string;
  label?: string;
  lastFour?: boolean;
  locked?: boolean;
}

const SsnField: React.FC<PropTypes> = ({
  fieldId,
  fieldName,
  label,
  lastFour,
  locked,
}) => {
  const [ssnHidden, setSsnHidden] = useState(true);
  const t = useTranslate();
  const lockedFields = useSelector(selectLockedFields);
  const { isLastFour, isVisible, isRequired } = useFieldCustomization();
  const isLastFourBool = isLastFour();
  const ssnLabel = t('formLabel.ssn', {
    context: isLastFourBool && 'last_four',
  });

  const toggleSsnHidden = () => {
    setSsnHidden(ssnHidden ? false : true);
  };

  const visibleButton = (
    <InputAdornment
      position="end"
      // setting the variant prevent mismatched className between SSR and the
      // client. Not entirely sure why there's a mismatch in the first place.
      variant="standard"
    >
      <IconButton
        aria-label="show ssn"
        aria-pressed={ssnHidden ? 'false' : 'true'}
        onClick={toggleSsnHidden}
        edge="end"
        data-cy="ssnVisibility"
      >
        {ssnHidden ? <VisibilityOffOutlined /> : <VisibilityOutlinedIcon />}
      </IconButton>
    </InputAdornment>
  );

  const InputComponentProps = {
    InputProps: {
      endAdornment: visibleButton,
    },
  };

  return (
    <React.Fragment>
      {isVisible('ssn') && (
        <Field name={fieldName || 'ssn'}>
          {(fieldProps: FieldProps) => (
            <FormTextField
              // "ssnField" is not a real value for autocomplete, but Chrome
              // (depending on version) doesn't respect the value "off". See this bug
              // comment: https://bugs.chromium.org/p/chromium/issues/detail?id=468153#c164
              autoComplete="ssnField"
              id={fieldId || 'creditAppSSN'}
              disabled={locked === undefined ? lockedFields.ssn : locked}
              fieldProps={fieldProps}
              required={isRequired('ssn')}
              label={label || withOptional(ssnLabel, isRequired('ssn'))}
              mask={ssnMask(lastFour === undefined ? isLastFourBool : lastFour)}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              guide={ssnHidden ? false : true}
              {...InputComponentProps}
              type={ssnHidden ? 'password' : 'text'}
            />
          )}
        </Field>
      )}
    </React.Fragment>
  );
};

export default SsnField;
